import { BreakpointObserver } from '@angular/cdk/layout';
import { Directive, ElementRef, HostBinding, HostListener, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { AdminLinkHelperService } from '@app/services/admin-link-helper.service';
import { LearningPathService } from '@app/services/api/learning-path.service';
import { AuthService } from '@app/services/auth.service';
import { BranchService } from '@app/services/branch.service';
import { LanguageSelectDialogService } from '@app/services/language-select-dialog.service';
import { NotificationService } from '@app/services/notification.service';
import { SearchDialogService } from '@app/services/search-dialog.service';
import { SendbirdService } from '@app/services/sendbird.service';
import { SettingsService } from '@app/services/settings.service';
import { LanguageStorageService, MenuItemKey, NavigationMenuItem, ProfileService, ProfileUserDTO, REGEX_LIST, SettingsModel } from 'library-explorer';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { map, mergeMap, takeUntil, tap } from 'rxjs/operators';

@Directive({
    selector: '[navigationElement]',
    inputs: ['isHeaderMenu'],
    standalone: false
})
export class NavigationElementDirective implements OnInit, OnDestroy {
  @Input() public navigationElement: NavigationMenuItem;

  @Input() public isHeaderMenu = false;

  @HostBinding('class.d-none') public hide = false;

  private unsubscribe: Subject<void> = new Subject();

  private initialized = false;

  constructor(
    @Inject(REGEX_LIST) public regexList: any,
    private readonly element: ElementRef,
    private readonly learningPathService: LearningPathService,
    private readonly branchService: BranchService,
    private readonly adminLinkHelperService: AdminLinkHelperService,
    private readonly authService: AuthService,
    private readonly sendBirdService: SendbirdService,
    private readonly profileService: ProfileService,
    private readonly searchDialogService: SearchDialogService,
    private readonly notificationService: NotificationService,
    private readonly languageStorageService: LanguageStorageService,
    private readonly languageSelectDialogService: LanguageSelectDialogService,
    private readonly settingsService: SettingsService) {
  }

  ngOnInit(): void {
    combineLatest([
      this.profileService.getCurrentProfile(),
      this.settingsService.getSettings()
    ])
      .pipe(
        tap(([_profile, settings]) => this.setupMenuItem(settings)),
        mergeMap(([profile, _settings]) => this.shouldBeHidden(profile))
      )
      .subscribe(shouldHide => {
        this.hide = shouldHide;
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  @HostListener('click', ['$event'])
  public menuItemAction(event: Event) {
    if (this.navigationElement?.path) {
      return;
    }

    const element = this.isHeaderMenu ? this.element.nativeElement : null;

    switch (this.navigationElement.key) {
      case MenuItemKey.CHAT:
        this.sendBirdService.openChatListDialog(element);
        break;
      case MenuItemKey.LANGUAGES:
        event.preventDefault();
        event.stopPropagation();
        this.openLanguageDialog(element);
        break;
      case MenuItemKey.SEARCH:
        this.searchDialogService.openSearchDialog();
        break;
      case MenuItemKey.NOTIFICATIONS:
        this.notificationService.openListDialog(element);
        break;
      case MenuItemKey.ADMIN_PANEL:
        this.navigateToAdminPlatform();
        break;
      case MenuItemKey.LOG_OUT:
        this.authService.logout();
        break;
    }
  }

  private openLanguageDialog(element: HTMLElement): void {
    const isAdmin = this.profileService.isCurrentUserIsAdmin();

    const languages = this.languageStorageService.getLanguages().filter(item => isAdmin || item.enabled);
    const contentLanguages = this.languageStorageService.getContentLanguages().filter(item => isAdmin || item.enabled);
    this.languageSelectDialogService.open(element, languages, contentLanguages);
  }

  private shouldBeHidden(profile: ProfileUserDTO): Observable<boolean> {
    switch (this.navigationElement.key) {
      case MenuItemKey.SWITCH_BRANCH:
        return this.branchService.getBranches()
          .pipe(
            map(items => items?.filter(item => item.isMember || item.selfEnrolment)?.length <= 1)
          );
      case MenuItemKey.LEARNING_PATH:
        return this.learningPathService.getLearningPathsForUser(profile.id)
          .pipe(
            map(data => data?.items.length === 0)
          );
      default:
        return of(false);
    }
  }

  private navigateToAdminPlatform(): void {
    this.adminLinkHelperService.navigateToAdminPlatform();
  }

  private setupMenuItem(settings: SettingsModel): void {
    if (this.initialized) {
      return;
    }

    switch (this.navigationElement.key) {
      case MenuItemKey.HELP:
        const supportUrl = this.isEmail(settings.legal.support?.url) ? `mailto:${settings.legal.support?.url}` : settings.legal.support?.url;
        Object.assign(this.navigationElement, { path: supportUrl });
        break;
      case MenuItemKey.CHAT:
        this.subscribeOnChatMessages();
        break;
      case MenuItemKey.NOTIFICATIONS:
        this.subscribeOnNotifications();
        break;
      case MenuItemKey.EVENTS:
        if (settings.webinars?.comingSoonPageEnabled) {
          Object.assign(this.navigationElement, { pageId: settings.webinars?.comingSoonPageId });
        }

        break;
    }

    this.initialized = true;
  }

  private subscribeOnChatMessages(): void {
    this.sendBirdService.unreadMessagesCount
      .pipe(
        takeUntil(this.unsubscribe)
      )
      .subscribe(counter => Object.assign(this.navigationElement, { counter }));
  }

  private subscribeOnNotifications(): void {
    this.notificationService.unreadNotificationsCount
      .pipe(
        takeUntil(this.unsubscribe)
      )
      .subscribe(counter => Object.assign(this.navigationElement, { counter }));
  }


  private isEmail(url: string): RegExpMatchArray {
    return url.match(this.regexList.email);
  }
}
